import React, { FC, useState } from "react";
import { v4 as uuidv4 } from "uuid";
import BlockButton from "./BlockButton";
import Column from "./Column";
import Modal, { ModalProps } from "./Modal";
import PlusButton from "./PlusButton";
import Row from "./Row";
import TimeInput from "./TimeInput";
import XButton from "./XButton";
import { ScreenTimeConfig, ScreenTimeConfigBlock } from "../models";

interface Props extends ModalProps {
  onSubmit: (value: ScreenTimeConfig | null) => any;
  config: ScreenTimeConfig | null;
  onClose: () => void;
}

/**
 * A modal component for configuring screen time blocks for specific days.
 * Allows users to add, edit, and delete time blocks for each day of the week.
 * Ensures that time blocks do not overlap and that start times are before end times.
 *
 * @component
 * @param {Props} props - The properties for the ConfigureScreenTimeModal component.
 * @param {ScreenTimeConfig[]} props.config - The initial configuration of screen time blocks.
 * @param {(value: ScreenTimeConfig[]) => any} props.onSubmit - Callback function invoked when the user submits the configuration.
 * @param {() => void} props.onClose - Callback function invoked when the modal is closed.
 *
 * @example
 * ```tsx
 * import React, { useState } from "react";
 * import ConfigureScreenTimeModal from "./ConfigureScreenTimeModal";
 * import { ScreenTimeConfigDays } from "../models";
 *
 * const App = () => {
 *   const [isModalOpen, setIsModalOpen] = useState(true);
 *   const initialConfig = [
 *     {
 *       day: ScreenTimeConfigDays.MONDAY,
 *       blocks: [
 *         { blockId: "1", start: 9, end: 12 },
 *         { blockId: "2", start: 14, end: 18 },
 *       ],
 *     },
 *   ];
 *
 *   const handleSubmit = (config) => {
 *     console.log("Updated Config:", config);
 *   };
 *
 *   return (
 *     <>
 *       {isModalOpen && (
 *         <ConfigureScreenTimeModal
 *           config={initialConfig}
 *           onSubmit={handleSubmit}
 *           onClose={() => setIsModalOpen(false)}
 *         />
 *       )}
 *     </>
 *   );
 * };
 *
 * export default App;
 * ```
 */
const ConfigureScreenTimeModal: FC<Props> = (props) => {
  const [
    screenTimeConfigValue,
    setScreenTimeConfigValue,
  ] = useState<ScreenTimeConfig>(
    props.config ?? {
      alldays: [],
      monday: [],
      tuesday: [],
      wednesday: [],
      thursday: [],
      friday: [],
      saturday: [],
      sunday: [],
    }
  );
  const [showError, setShowError] = useState<string | null>(null);

  const validateBlock = (
    block: { blockId: string; start: number; end: number },
    day: keyof ScreenTimeConfig
  ) => {
    if (block.start >= block.end) {
      return false; // Invalid block if start time is not before end time
    } else {
      let isValid = true;
      screenTimeConfigValue.alldays
        .concat(screenTimeConfigValue[day] || [])
        .filter((configuredBlock) => configuredBlock.blockId !== block.blockId)
        .forEach((configuredBlock) => {
          if (
            block.end > configuredBlock.start &&
            configuredBlock.end > block.start
          ) {
            isValid = false; // Overlapping blocks are not allowed
          }
        });
      return isValid; // Valid block
    }
  };

  const handleBlockChange = (
    newTime: number,
    day: keyof ScreenTimeConfig,
    blockId: string,
    timeIndex: number
  ) => {
    const newBlocks:
      | ScreenTimeConfigBlock[]
      | undefined = screenTimeConfigValue[day].map((block) => {
      if (block.blockId === blockId) {
        const newBlock = { ...block };
        if (timeIndex === 0) {
          newBlock.start = newTime;
        } else {
          newBlock.end = newTime;
        }
        return newBlock;
      }
      return block;
    });
    if (newBlocks) {
      setScreenTimeConfigValue((prev) => ({
        ...prev,
        [day]: newBlocks,
      }));
    } else {
      console.error("Invalid block change");
      showBlockUpdateError(
        "Blocks cannot overlap and end time cannot be before start time."
      );
    }
  };

  const handleBlockDelete = (day: keyof ScreenTimeConfig, blockId: string) => {
    const newBlocks = screenTimeConfigValue[day].filter(
      (block) => block.blockId !== blockId
    );
    setScreenTimeConfigValue((prev) => ({
      ...prev,
      [day]: newBlocks,
    }));
  };

  const handleBlockInsert = (day: keyof ScreenTimeConfig) => {
    let latestBlockEndTime = 0;
    if (day === "alldays") {
      const allBlocks = Object.keys(screenTimeConfigValue).flatMap(
        (key) => screenTimeConfigValue[key as keyof ScreenTimeConfig]
      );
      latestBlockEndTime =
        allBlocks.sort((a, b) => a.end - b.end).at(-1)?.end || 0;
    } else {
      latestBlockEndTime =
        screenTimeConfigValue[day]
          .concat(screenTimeConfigValue.alldays)
          .sort((a, b) => a.end - b.end)
          .at(-1)?.end || 0;
    }

    if (latestBlockEndTime >= 24) {
      // Cannot add more blocks if the latest block ends at or after 24:00
      showBlockUpdateError("Cannot add more blocks after 24:00.");
      return;
    }

    const newBlocks = [
      ...screenTimeConfigValue[day],
      {
        blockId: uuidv4(),
        start: latestBlockEndTime,
        end: latestBlockEndTime + 1,
      },
    ];

    setScreenTimeConfigValue((prev) => ({ ...prev, [day]: newBlocks }));
  };

  const dayKeys: Array<keyof ScreenTimeConfig> = [
    "alldays",
    "monday",
    "tuesday",
    "wednesday",
    "thursday",
    "friday",
    "saturday",
    "sunday",
  ];

  const handleSubmit = () => {
    let numDaysConfigured = 0;
    dayKeys.forEach((day) => {
      if (screenTimeConfigValue[day].length > 0) {
        numDaysConfigured++;
      }
    });

    const allDaysConfigured =
      numDaysConfigured === dayKeys.length - 1 ||
      screenTimeConfigValue.alldays.length > 0;

    const emptyConfig = dayKeys.concat(["alldays"]).every((day) => {
      return screenTimeConfigValue[day].length === 0;
    });

    const validation = dayKeys.map((day) => {
      return screenTimeConfigValue[day].every((block) =>
        validateBlock(block, day)
      );
    });

    // Only submit if all blocks are valid and all days are configured, or if the config is empty
    if (!validation.includes(false) && allDaysConfigured) {
      props.onSubmit(screenTimeConfigValue);
      props.onClose();
    } else if (emptyConfig) {
      props.onSubmit(null);
      props.onClose();
    } else if (validation.includes(false)) {
      showBlockUpdateError(
        "Blocks cannot overlap and end time cannot be before start time."
      );
    } else if (!allDaysConfigured) {
      showBlockUpdateError("Please configure blocks for all days.");
    }
  };

  const showBlockUpdateError = (message: string) => {
    setShowError(message);
    setTimeout(() => {
      setShowError(null);
    }, 3000);
  };

  return (
    <Modal disableClose={true} onClose={() => props.onClose()}>
      <Column style={{ alignItems: "center" }}>
        <h1>Configure Screen Time</h1>
        <div
          style={{
            marginBottom: 24,
          }}
        >
          {showError ? (
            <p style={{ color: "red", textAlign: "center" }}>{showError}</p>
          ) : (
            <p style={{ textAlign: "center" }}>
              Click the '+' button to add a screen time block.
            </p>
          )}
          {dayKeys.map((day) => (
            <ScreenTimeConfigRow
              key={day}
              config={screenTimeConfigValue}
              day={day}
              handleBlockInsert={handleBlockInsert}
              handleBlockChange={handleBlockChange}
              handleBlockDelete={handleBlockDelete}
            />
          ))}
        </div>
        <BlockButton onClick={handleSubmit}>Save</BlockButton>
      </Column>
    </Modal>
  );
};

const dayMapping: { [key: string]: string } = {
  alldays: "All Days",
  monday: "Monday",
  tuesday: "Tuesday",
  wednesday: "Wednesday",
  thursday: "Thursday",
  friday: "Friday",
  saturday: "Saturday",
  sunday: "Sunday",
};

const ScreenTimeConfigRow: FC<{
  config: ScreenTimeConfig;
  day: keyof ScreenTimeConfig;
  handleBlockInsert: (day: keyof ScreenTimeConfig) => void;
  handleBlockChange: (
    hour: number,
    day: keyof ScreenTimeConfig,
    blockId: string,
    timeIndex: number
  ) => void;
  handleBlockDelete: (day: keyof ScreenTimeConfig, blockId: string) => void;
}> = ({
  config,
  day,
  handleBlockInsert,
  handleBlockChange,
  handleBlockDelete,
}) => (
  <Row
    key={day}
    style={{
      justifyContent: "space-between",
      width: "100%",
      marginTop: 4,
      marginBottom: 4,
      borderBottom: "1px solid #ccc",
      paddingBottom: 4,
      paddingTop: 4,
    }}
  >
    <Column style={{ justifyContent: "center" }}>
      <h3>{dayMapping[day]}</h3>
    </Column>
    <Column
      style={{
        marginLeft: 8,
        marginRight: 8,
        justifyContent: "center",
      }}
    >
      {(() => {
        const blocks = config[day] === undefined ? [] : config[day];
        if (blocks.length === 0) {
          return <div style={{ width: 200 }} />;
        }
        // Split blocks for each day into rows of 3
        const rows = [];
        for (let i = 0; i < blocks.length; i += 3) {
          rows.push(blocks.slice(i, i + 3));
        }
        return rows.map((rowBlocks, rowIdx) => (
          <Row key={rowIdx} style={{ marginBottom: 4 }}>
            {rowBlocks.map((block) => (
              <Row
                key={block.blockId}
                style={{
                  alignItems: "center",
                  backgroundColor: "#F2D0BD",
                  padding: 4,
                  marginLeft: 4,
                  marginRight: 4,
                  borderRadius: 8,
                  border: "1px solid #ccc",
                }}
              >
                <TimeInput
                  onChange={(event) =>
                    handleBlockChange(event.hour, day, block.blockId, 0)
                  }
                  value={{ hour: block.start, minute: 0 }}
                  minHour={0}
                  maxHour={23}
                  displayMin={false}
                />
                <span style={{ marginLeft: 4, marginRight: 4 }}>to</span>
                <TimeInput
                  onChange={(event) =>
                    handleBlockChange(event.hour, day, block.blockId, 1)
                  }
                  value={{ hour: block.end, minute: 0 }}
                  minHour={1}
                  maxHour={24}
                  displayMin={false}
                />
                <div style={{ marginLeft: 8 }} />
                <XButton
                  onClick={() => handleBlockDelete(day, block.blockId)}
                />
              </Row>
            ))}
          </Row>
        ));
      })()}
    </Column>
    <Column style={{ justifyContent: "end", paddingBottom: 4 }}>
      <PlusButton onClick={() => handleBlockInsert(day)} />
    </Column>
  </Row>
);

export default ConfigureScreenTimeModal;
